// Shared error surface for the hand-written domain hooks. A mutation NEVER reports success on an unpersisted
// write: every hook throws ApiError on a non-2xx, and its message is the server's
// ProblemDetails.detail/title verbatim — the toast/error state shows exactly what the server said.

/** A typed API error carrying the HTTP status + the server's ProblemDetails (RFC 7807). */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem?: unknown,
  ) {
    super(problemDetail(problem) ?? `Request failed (${status})`);
    this.name = "ApiError";
  }
}

/** Pull the human-readable detail/title out of a ProblemDetails body (openapi-fetch's `error`). */
export function problemDetail(problem: unknown): string | undefined {
  if (problem && typeof problem === "object") {
    const p = problem as { detail?: string; title?: string };
    return p.detail ?? p.title;
  }
  return undefined;
}

/** The message to show for a caught error, falling back when it carries none. */
export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * A form-friendly message: prefer the FIRST field-level validation message (RFC 7807 `errors`) — e.g.
 * "A fan with this email already exists." — over the generic "Validation failed" title, so a 400 shows the
 * specific reason. Falls back to the detail/title, then the caller's fallback.
 */
export function formErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.problem && typeof err.problem === "object") {
    const errors = (err.problem as { errors?: Record<string, string[]> }).errors;
    if (errors) {
      for (const messages of Object.values(errors)) {
        if (messages?.length) return messages[0];
      }
    }
  }
  return errorMessage(err, fallback);
}
